import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Printer } from "lucide-react";
import { apiClient } from "@/lib/auth";
import { GRANT_ORDER, Trend, fmtGrant, useGrantOutcomes } from "@/components/nds/grants/GrantsKpiRow";

const field = "[color-scheme:dark] bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver font-mono text-[11px] px-2 py-1.5 rounded-sm";
const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 border border-nex-gold/50 text-nex-gold font-mono text-[10px] tracking-[0.18em] uppercase rounded-sm hover:bg-nex-gold/10 transition-colors";

/** One Grants tile, opened: the number plus who/what is behind it. */
export function GrantTileContent({ win }) {
  const key = win.payload?.key;
  const [s, setS] = useState({ loading: true });
  useEffect(() => {
    apiClient.get(`/grants/outcomes/${key}`).then(({ data }) => setS({ data })).catch((e) => setS({ error: e?.response?.data?.detail || "Couldn't load this tile" }));
  }, [key]);
  if (s.loading) return <div className="p-6 flex items-center gap-2 text-nex-muted" data-testid="grant-tile-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (s.error) return <p className="p-6 text-red-300 text-sm" data-testid="grant-tile-error">{s.error}</p>;
  const { tile: t, rows, date_from: f, date_to: to } = s.data;
  return (
    <div className="p-5 space-y-4" data-testid={`grant-window-${key}`}>
      <div>
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">{t.label} · {t.window === "current" ? "current" : `${f} → ${to}`}</div>
        <div className="mt-1 text-4xl font-serif text-nex-silver" data-testid="grant-window-value">{fmtGrant(t)}</div>
        {key === "conduct" && <Trend delta={t.delta} />}
        <div className="text-sm text-nex-muted">{t.sub}</div>
        {t.note && <div className="mt-1 text-[11px] text-amber-300" data-testid="grant-window-note">{t.note}</div>}
      </div>
      <ul className="divide-y divide-white/5 border border-white/10 rounded-sm" data-testid="grant-window-rows">
        {rows.map((r, i) => <li key={i} className="px-3 py-2 flex items-center justify-between gap-3 text-sm"><span className="text-nex-silver truncate">{r.name}</span><span className="font-mono text-[11px] text-nex-muted shrink-0">{r.value}</span></li>)}
        {!rows.length && <li className="px-3 py-6 text-center text-sm text-nex-muted" data-testid="grant-window-empty">Nothing in this range.</li>}
      </ul>
    </div>
  );
}

const lines = (d) => [`TEA · Grant report · ${d.date_from} to ${d.date_to}`,
  ...GRANT_ORDER.filter((k) => d.tiles[k]).map((k) => { const t = d.tiles[k]; return `${t.label}: ${fmtGrant(t)}${t.format === "score" ? "/100" : ""} (${t.sub})${t.note ? ` — ${t.note}` : ""}`; })];

/** Grant report: the same Grants numbers for any date range, ready to copy or print. */
export function GrantReportContent() {
  const [draft, setDraft] = useState(null);
  const [range, setRange] = useState(null);
  const stable = useMemo(() => range, [range]);
  const { data, error } = useGrantOutcomes(stable);
  useEffect(() => { if (data && !draft) setDraft({ date_from: data.date_from, date_to: data.date_to }); }, [data, draft]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(lines(data).join("\n")); toast.success("Grant report copied"); } catch { toast.error("Couldn't copy — select the text instead"); }
  };
  const print = () => {
    const w = window.open("", "_blank", "width=720,height=800");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(`<html><head><title>Grant report</title></head><body style="font-family:Georgia,serif;padding:32px">${lines(data).map((l, i) => (i ? `<p>${l}</p>` : `<h2>${l}</h2>`)).join("")}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <div className="p-5 space-y-4" data-testid="grant-report">
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1"><span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-nex-muted">From</span>
          <input type="date" value={draft?.date_from || ""} onChange={(e) => setDraft((x) => ({ ...x, date_from: e.target.value }))} data-testid="grant-report-from" className={field} /></label>
        <label className="space-y-1"><span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-nex-muted">To</span>
          <input type="date" value={draft?.date_to || ""} onChange={(e) => setDraft((x) => ({ ...x, date_to: e.target.value }))} data-testid="grant-report-to" className={field} /></label>
        <button type="button" disabled={!draft?.date_from || !draft?.date_to} onClick={() => setRange({ ...draft })} data-testid="grant-report-apply" className={`${btn} disabled:opacity-40`}>Apply</button>
        <div className="ml-auto flex gap-2">
          <button type="button" disabled={!data} onClick={copy} data-testid="grant-report-copy" className={btn}><Copy className="h-3.5 w-3.5" /> Copy</button>
          <button type="button" disabled={!data} onClick={print} data-testid="grant-report-print" className={btn}><Printer className="h-3.5 w-3.5" /> Print</button>
        </div>
      </div>
      {error && <p className="text-red-300 text-sm" data-testid="grant-report-error">{error}</p>}
      {!data ? <div className="flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
        <table className="w-full text-sm" data-testid="grant-report-table">
          <caption className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-nex-muted pb-2" data-testid="grant-report-range">{data.date_from} → {data.date_to}</caption>
          <tbody>
            {GRANT_ORDER.filter((k) => data.tiles[k]).map((k) => { const t = data.tiles[k]; return (
              <tr key={k} className="border-t border-white/10" data-testid={`grant-report-row-${k}`}>
                <td className="py-2 pr-3 font-mono text-[10px] uppercase tracking-[0.14em] text-nex-muted">{t.label}</td>
                <td className="py-2 pr-3 font-serif text-lg text-nex-silver" data-testid={`grant-report-value-${k}`}>{fmtGrant(t)}{t.format === "score" ? "/100" : ""}</td>
                <td className="py-2 text-xs text-nex-muted">{t.sub}{t.note ? <span className="block text-amber-300">{t.note}</span> : null}</td>
              </tr>); })}
          </tbody>
        </table>
      )}
    </div>
  );
}
